
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { devLog, devError } from "@/utils/devLog";
import type { Profile } from "@/types/profile";
import { useAuth } from "@/providers/AuthProvider";
import { useEffect } from "react";

export const useUserProfile = (userId?: string) => {
  devLog("[useUserProfile] ➡️ Hook Called", { userId });
  const { user, initialSessionProcessed } = useAuth();

  const effectiveUserId = userId || user?.id;

  const { 
    data: profile, 
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile 
  } = useQuery<Profile | null>({
    queryKey: ['userProfileComplete', effectiveUserId],
    queryFn: async () => {
      devLog("[useUserProfile] ➡️ Fetching Profile", { userId: effectiveUserId });

      if (!effectiveUserId) {
        devLog("[useUserProfile] ⚠️ No User ID Available");
        return null;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', effectiveUserId)
          .single();

        if (error) {
          devError("[useUserProfile] ❌ Profile Fetch Error:", error);
          return null;
        }

        devLog("[useUserProfile] ✅ Profile Fetch Result", {
          hasUsername: !!data.username,
          role: data.role,
        });

        return data as Profile;
      } catch (error) {
        devError("[useUserProfile] ❌ Unexpected error in profile fetch:", error);
        return null;
      }
    },
    enabled: !!effectiveUserId && initialSessionProcessed,
    staleTime: 0,
  });

  const isLoading = !initialSessionProcessed || profileLoading;
  
  // Only log on state changes, not on every render
  useEffect(() => {
    console.log("[useUserProfile] Hook result:", {
      hasProfile: !!profile,
      role: profile?.role,
      username: profile?.username,
      isLoading,
      error: profileError ? 'has error' : 'no error'
    });
  }, [profile, profileError, isLoading]);

  return {
    userData: user,
    profile,
    isLoading,
    isError: profileError,
    refetchProfile,
    role: profile?.role || null,
    username: profile?.username || null,
  };
};
